import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

// Types matching server response
interface CutListPart {
  label: string;
  width: number;
  height: number;
  areaName: string;
  sourceItemId: string;
  qty: number;
}

interface CutListEntry {
  materialId: string;
  materialName: string;
  materialType: 'sheet' | 'order' | 'skipped';
  sheetSize: string | null;
  sheetsNeeded: number | null;
  costPerSheet: number | null;
  totalCost: number;
  efficiency: number | null;
  unit: string;
  totalQty: number;
  parts: CutListPart[];
}

interface PlacedPart {
  part: {
    label: string;
    sourceArea: string;
    sourceItemId: string;
    canRotate: boolean;
    materialId: string;
  };
  x: number;
  y: number;
  rotated: boolean;
  width: number;
  height: number;
}

interface Sheet {
  width: number;
  height: number;
  materialId: string;
  materialName: string;
  placements: PlacedPart[];
}

interface MaterialNestingResult {
  materialId: string;
  materialName: string;
  sheetW: number;
  sheetH: number;
  costPerSheet: number;
  sheets: Sheet[];
  totalSheets: number;
  totalCost: number;
  efficiency: number;
  totalParts: number;
}

interface CncMaterialResult {
  materialId: string;
  materialName: string;
  totalSheets: number;
  totalParts: number;
  totalCutInches: number;
  totalTimeMin: number;
  operatorCost: number;
  machineCost: number;
  toolingCost: number;
  totalCncCost: number;
}

interface CncRunResult {
  materials: Record<string, CncMaterialResult>;
  totalTimeMin: number;
  totalCncCost: number;
}

interface NestingRunResult {
  materials: Record<string, MaterialNestingResult>;
  orderParts: Record<string, any[]>;
  itemCosts: Record<string, number>;
  cncCosts?: CncRunResult;
  itemCncCosts?: Record<string, number>;
}

export type { CutListEntry, CutListPart, Sheet, PlacedPart, MaterialNestingResult, NestingRunResult, CncRunResult, CncMaterialResult };

/** Trigger nesting optimization for a project */
export function useRunNesting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) => {
      const { data } = await apiClient.post<NestingRunResult>(
        `/nesting/project/${projectId}/run`,
      );
      return data;
    },
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['nesting', projectId] });
      queryClient.invalidateQueries({ queryKey: ['calculations', 'project', projectId] });
    },
  });
}

/** Fetch cut list for a project */
export function useNestingCutList(projectId: string | undefined) {
  return useQuery({
    queryKey: ['nesting', projectId, 'cut-list'],
    queryFn: async () => {
      const { data } = await apiClient.get<CutListEntry[]>(
        `/nesting/project/${projectId}/cut-list`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

/** Fetch full nesting results with sheet layouts */
export function useNestingResults(projectId: string | undefined) {
  return useQuery({
    queryKey: ['nesting', projectId, 'results'],
    queryFn: async () => {
      const { data } = await apiClient.get<NestingRunResult>(
        `/nesting/project/${projectId}/results`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}
